'use client'

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { pct } from '@/lib/format'
import type { SandboxCompetitor, SandboxBrand, SandboxPrompt } from '../data/types'
import type { Engine } from '@/lib/ai/constants'
import { BrandTag } from '../primitives/brand-tag'

const ENGINES: Engine[] = ['chatgpt', 'perplexity', 'google_aio']
const ENGINE_LABELS: Partial<Record<Engine, string>> = {
  chatgpt: 'ChatGPT',
  perplexity: 'Perplexity',
  google_aio: 'Google AI Overview',
}

interface CompetitorEntry {
  domain: string
  name: string
  shareOfVoice: number
  perEngine: Partial<Record<Engine, number>>
  isYou?: boolean
  promptsWon?: string[]
}

function buildTableRows(
  brand: SandboxBrand,
  brandSoV: number,
  brandPerEngine: Partial<Record<Engine, number>>,
  competitors: SandboxCompetitor[]
): CompetitorEntry[] {
  const allRows: CompetitorEntry[] = [
    {
      domain: brand.domain,
      name: brand.name,
      shareOfVoice: brandSoV,
      perEngine: brandPerEngine,
      isYou: true,
    },
    ...competitors.map((c) => ({
      domain: c.domain,
      name: c.name,
      shareOfVoice: c.shareOfVoice,
      perEngine: c.perEngine,
      promptsWon: c.promptsWon,
    })),
  ]

  return allRows.sort((a, b) => b.shareOfVoice - a.shareOfVoice)
}

interface CompetitorDetailPanelProps {
  competitor: SandboxCompetitor | null
  prompts: SandboxPrompt[]
  open: boolean
  onClose: () => void
}

function CompetitorDetailPanel({ competitor, prompts, open, onClose }: CompetitorDetailPanelProps) {
  if (!competitor) return null

  const wonPrompts = (competitor.promptsWon ?? [])
    .map((id) => prompts.find((p) => p.id === id))
    .filter((p): p is SandboxPrompt => Boolean(p))

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-72 sm:w-80 overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle className="text-base">{competitor.name}</SheetTitle>
          <p className="text-xs text-muted-foreground">{competitor.domain}</p>
        </SheetHeader>

        <div className="space-y-4">
          <div>
            <p className="mb-2 u-label">Per-engine score</p>
            <div className="space-y-1.5">
              {ENGINES.map((engine) => (
                <div key={engine} className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{ENGINE_LABELS[engine]}</span>
                  <span className="text-xs font-mono tabular-nums">
                    {competitor.perEngine[engine]}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {wonPrompts.length > 0 && (
            <div>
              <p className="mb-2 u-label">Prompts won ({wonPrompts.length})</p>
              <ul className="space-y-1.5">
                {wonPrompts.map((p) => (
                  <li
                    key={p.id}
                    className="rounded-md bg-muted px-2 py-1.5 text-xs text-foreground"
                  >
                    {p.text}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

interface CompetitorsPageProps {
  brand: SandboxBrand
  /** The brand's headline visibility and per-engine scores: the latest point the Visibility page charts. */
  brandShare: number
  brandPerEngine: Partial<Record<Engine, number>>
  competitors: SandboxCompetitor[]
  prompts: SandboxPrompt[]
  selectedCompetitorId: string | null
  onSelectCompetitor: (id: string | null) => void
}

export function CompetitorsPage({
  brand,
  brandShare,
  brandPerEngine,
  competitors,
  prompts,
  selectedCompetitorId,
  onSelectCompetitor,
}: CompetitorsPageProps) {
  const rows = buildTableRows(brand, brandShare, brandPerEngine, competitors)
  const selectedCompetitor = competitors.find((c) => c.domain === selectedCompetitorId) ?? null

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[480px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="py-1.5 pl-3 pr-2 text-left u-label w-8">#</th>
            <th className="py-1.5 px-2 text-left u-label">Brand</th>
            <th className="py-1.5 px-2 text-left u-label">Share of voice</th>
            {ENGINES.map((engine) => (
              <th key={engine} className="py-1.5 px-2 text-right u-label">
                {ENGINE_LABELS[engine]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((entry, index) => (
            <tr
              key={entry.domain}
              className={cn(
                'border-b border-border transition-colors duration-150',
                entry.isYou ? 'bg-series-1/10' : 'cursor-pointer hover:bg-muted/50'
              )}
              onClick={!entry.isYou ? () => onSelectCompetitor(entry.domain) : undefined}
            >
              <td className="py-2 pl-3 pr-2 text-xs text-muted-foreground tabular-nums">
                {index + 1}
              </td>
              <td className="py-2 px-2">
                <div className="flex items-center gap-1.5">
                  {entry.isYou ? (
                    <span className="text-xs font-medium text-foreground">{entry.name}</span>
                  ) : (
                    <button
                      type="button"
                      aria-haspopup="dialog"
                      className="focus-ring rounded-sm text-left text-xs font-medium text-foreground"
                    >
                      {entry.name}
                    </button>
                  )}
                  {entry.isYou && <BrandTag />}
                </div>
              </td>
              <td className="py-2 px-2">
                <div className="flex items-center gap-2">
                  <div className="relative h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn(
                        'absolute left-0 top-0 h-full rounded-full',
                        entry.isYou ? 'bg-series-1' : 'bg-muted-foreground/50'
                      )}
                      style={{ width: `${entry.shareOfVoice}%` }}
                    />
                  </div>
                  <span className="text-xs tabular-nums font-mono">
                    {pct(entry.shareOfVoice / 100)}
                  </span>
                </div>
              </td>
              {ENGINES.map((engine) => (
                <td key={engine} className="py-2 px-2 text-xs font-mono tabular-nums text-right">
                  {entry.perEngine[engine]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <CompetitorDetailPanel
        competitor={selectedCompetitor}
        prompts={prompts}
        open={Boolean(selectedCompetitorId)}
        onClose={() => onSelectCompetitor(null)}
      />
    </div>
  )
}
